import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, PenTool, RefreshCw, ArrowRight, Upload } from "lucide-react";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";

const STYLES = [
  { key: "modern", label: "Modern" }, { key: "minimal", label: "Minimal" }, { key: "playful", label: "Playful" },
  { key: "vintage", label: "Vintage badge" }, { key: "bold", label: "Bold" },
];
const field = "w-full border border-black/15 h-11 px-3 text-sm bg-white focus:outline-none focus:border-[#ED5A24]";
const label = "text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B] mb-2 block";

const toFile = async (dataUrl) => {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], "sketch-logo.png", { type: blob.type || "image/png" });
};

export default function Sketch() {
  const navigate = useNavigate();
  const [sketch, setSketch] = useState(null);
  const [form, setForm] = useState(() => JSON.parse(sessionStorage.getItem("sketch-form") || "null") || { business_name: "", tagline: "", style: "modern", colours: "" });
  useEffect(() => { sessionStorage.setItem("sketch-form", JSON.stringify(form)); }, [form]);
  const [image, setImageState] = useState(() => sessionStorage.getItem("sketch-last") || null);
  const setImage = (img) => {
    setImageState(img);
    try { sessionStorage.setItem("sketch-last", img); } catch { /* storage full: keep in memory only */ }
  };
  const [left, setLeft] = useState(undefined);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/sketch/tries").then(({ data }) => setLeft(data.left)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async () => {
    if (!sketch) return toast.error("Upload a photo of your sketch first");
    if (!form.business_name.trim()) return toast.error("Add your business name");
    setBusy("gen");
    try {
      const fd = new FormData();
      fd.append("file", sketch);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const { data } = await api.post("/sketch/generate", fd, { timeout: 180000 });
      setImage(data.image);
      setLeft(data.left);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't create a logo — please try again");
    } finally { setBusy(""); }
  };

  const useLogo = async () => {
    setBusy("use");
    try {
      const fd = new FormData();
      fd.append("file", await toFile(image));
      fd.append("name", form.business_name.trim());
      const { data } = await api.post("/vectorize", fd, { timeout: 180000 });
      navigate(`/dashboard?job=${data.job_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't convert the logo");
      setBusy("");
    }
  };

  const noTries = left === 0;
  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]" data-testid="sketch-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 md:py-16">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#ED5A24] mb-3 flex items-center gap-2"><PenTool className="w-3.5 h-3.5" /> Sketch to logo</div>
        <h1 className="text-4xl sm:text-5xl tracking-tighter font-black mb-4">Turn your sketch into a real logo.</h1>
        <p className="text-base text-[#52525B] max-w-2xl mb-10">Snap a photo of your napkin doodle or concept drawing. We'll redraw it as a clean, professional logo, then turn it into vector files. Free to try — A$10 to unlock the files of the one you love.</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5 space-y-5">
            <SketchUpload sketch={sketch} setSketch={setSketch} />
            <div><label className={label} htmlFor="sk-name">Business name</label>
              <input id="sk-name" className={field} value={form.business_name} onChange={set("business_name")} maxLength={60} placeholder="e.g. Swim Nest" data-testid="sketch-name-input" /></div>
            <div><label className={label} htmlFor="sk-tag">Tagline (optional)</label>
              <input id="sk-tag" className={field} value={form.tagline} onChange={set("tagline")} maxLength={80} placeholder="e.g. Swim School" data-testid="sketch-tagline-input" /></div>
            <div><span className={label}>Style</span>
              <div className="flex flex-wrap gap-2">{STYLES.map((s) => (
                <button key={s.key} type="button" onClick={() => setForm((f) => ({ ...f, style: s.key }))} data-testid={`sketch-style-${s.key}`}
                  className={`border px-4 h-10 text-sm transition-colors duration-200 ${form.style === s.key ? "border-[#ED5A24] bg-[#FDF0EA] font-bold" : "border-black/15 hover:border-[#0A0A0B]"}`}>{s.label}</button>
              ))}</div></div>
            <div><label className={label} htmlFor="sk-col">Colours (optional)</label>
              <input id="sk-col" className={field} value={form.colours} onChange={set("colours")} maxLength={80} placeholder="e.g. navy and aqua" data-testid="sketch-colours-input" /></div>
            <button type="button" onClick={generate} disabled={!!busy || noTries} data-testid="sketch-generate-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 transition-colors duration-200 disabled:opacity-50">
              {busy === "gen" ? <><Loader2 className="w-4 h-4 animate-spin" /> Designing your logo… (up to a minute)</> : image ? <><RefreshCw className="w-4 h-4" /> Try again</> : "Create my logo"}
            </button>
            {left !== undefined && left !== null && (
              <p className="text-xs text-[#52525B]" data-testid="sketch-tries-left">{noTries ? "No free tries left today — come back tomorrow." : `${left} free ${left === 1 ? "try" : "tries"} left today`}</p>
            )}
          </div>
          <SketchResult image={image} busy={busy} onUse={useLogo} />
        </div>
      </div>
    </div>
  );
}

const SketchUpload = ({ sketch, setSketch }) => {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!sketch) return setPreview(null);
    const url = URL.createObjectURL(sketch);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [sketch]);
  const pick = (f) => {
    if (!f) return;
    if (!/image\/(png|jpe?g|webp)/.test(f.type)) return toast.error("Use a PNG, JPG or WEBP photo");
    if (f.size > 10 * 1024 * 1024) return toast.error("File too large — maximum 10 MB");
    setSketch(f);
  };
  return (
    <label className="block border-2 border-dashed border-black/15 hover:border-[#ED5A24] bg-[#F8F9FA] cursor-pointer transition-colors duration-200" data-testid="sketch-dropzone"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}>
      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="sketch-file-input" />
      {preview ? <img src={preview} alt="Your sketch" className="w-full h-56 object-contain bg-white" />
        : <div className="h-56 flex flex-col items-center justify-center gap-2 text-sm text-[#52525B]"><Upload className="w-6 h-6 text-[#ED5A24]" />Upload a photo of your sketch</div>}
    </label>
  );
};

const SketchResult = ({ image, busy, onUse }) => (
  <div className="md:col-span-7 border border-black/10 bg-[#F8F9FA] p-6 flex flex-col" data-testid="sketch-result">
    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B] mb-4">Your logo</div>
    <div className="flex-1 min-h-[320px] bg-white border border-black/5 flex items-center justify-center p-6">
      {busy === "gen" ? <Loader2 className="w-8 h-8 animate-spin text-[#ED5A24]" />
        : image ? <img src={image} alt="Generated logo" className="max-h-[440px] max-w-full object-contain" data-testid="sketch-result-image" />
        : <p className="text-sm text-[#52525B] text-center max-w-xs">Your new logo appears here. Not quite right? Tap Try again for another take.</p>}
    </div>
    {image && (
      <button type="button" onClick={onUse} disabled={!!busy} data-testid="sketch-use-btn"
        className="mt-4 inline-flex items-center justify-center gap-2 bg-[#ED5A24] hover:bg-[#0A0A0B] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 transition-colors duration-200 disabled:opacity-50">
        {busy === "use" ? <><Loader2 className="w-4 h-4 animate-spin" /> Converting to vector…</> : <>Use this logo <ArrowRight className="w-4 h-4" /></>}
      </button>
    )}
  </div>
);
